import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { useOptimizedProfileData } from "@/hooks/profile/useOptimizedProfileData";
import { useNotifications } from "@/hooks/useNotifications";
import { useUserVerification } from "@/components/profile/hooks/useUserVerification";
import { useSidebarActiveContext } from "@/contexts/SidebarActiveContext";
import { AccountMenuBody } from "./AccountMenuBody";

interface MobileAccountSheetProps {
  // Renders a custom trigger (e.g. the bottom bar's "You" tab) instead of
  // the default header avatar button.
  renderTrigger?: (onClick: () => void) => React.ReactNode;
}

export function MobileAccountSheet({ renderTrigger }: MobileAccountSheetProps) {
  const { user, userRole, signOut } = useAuth();
  const { profile } = useOptimizedProfileData(user?.id);
  const { unreadCount } = useNotifications();
  const { isVerified } = useUserVerification(user);
  const { accountSheetOpen: open, setAccountSheetOpen: setOpen } =
    useSidebarActiveContext();
  const navigate = useNavigate();

  if (!user) return null;

  const displayName =
    profile?.display_name || profile?.name || user.email?.split("@")[0] || "User";

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success("Signed out successfully");
      navigate("/");
    } catch (error) {
      console.error("Sign out error:", error);
      toast.error("Failed to sign out");
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      {renderTrigger ? (
        renderTrigger(() => setOpen(true))
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex items-center gap-[7px]"
        >
          <img
            src={profile?.avatar_url || "/placeholder.svg"}
            alt={displayName}
            className="h-[34px] w-[34px] flex-shrink-0 rounded-full object-cover bg-[#EFEFF7]"
          />
        </button>
      )}
      <SheetContent
        side="bottom"
        hideCloseButton
        overlayClassName="z-[68]"
        className="light z-[68] font-archivo p-0 rounded-t-[3px] border-0 max-h-[85vh] overflow-y-auto"
      >
        <div className="flex items-center gap-3 px-[18px] pt-5 pb-[18px] border-b border-[#1E1B4B]/10">
          <img
            src={profile?.avatar_url || "/placeholder.svg"}
            alt={displayName}
            className="h-[46px] w-[46px] flex-shrink-0 rounded-full object-cover bg-[#EFEFF7]"
          />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[15px] leading-tight text-[#1E1B4B] truncate">
              {displayName}
            </div>
            <div className="mt-[3px] text-xs leading-tight text-[#6E6B8A] capitalize">
              {userRole || "Guest"}
              {!isVerified && " · Free plan"}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="h-11 w-11 flex-shrink-0 flex items-center justify-center bg-[#F7F7FB] rounded-[2px]"
          >
            <X size={18} className="text-[#6E6B8A]" strokeWidth={1.9} />
          </button>
        </div>
        <AccountMenuBody
          variant="light"
          displayName={displayName}
          role={userRole}
          isVerified={isVerified}
          unreadNotifications={unreadCount}
          showHeader={false}
          onNavigate={() => setOpen(false)}
          onSignOut={handleSignOut}
        />
      </SheetContent>
    </Sheet>
  );
}
